// node modules
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import withStyles from 'isomorphic-style-loader/withStyles';
import className from 'classnames';
import ResizeObserver from 'resize-observer-polyfill';

// app modules: components
import Advert from 'components/Advert';

// app modules: props
import SearchResultsMatrixProps from 'components/SearchResultsMatrix/SearchResultsMatrixProps';

// app modules: constants
import { EZOIC_INFINITE_SCROLL_PLACEMENT_IDS } from 'constants/index';

// assets
import CssSearchResultsMatrix from './SearchResultsMatrix.scss';

const PADDING = 30;
class SearchResultsMatrix extends Component {
  static propTypes = SearchResultsMatrixProps;

  static defaultProps = {
    children: null,
    ItemChildren: () => null,
    itemWidth: null,
    showAll: false,
    onItemClick: () => {},
    headingRenderer: () => {},
    prefixItems: [],
  };

  constructor(props) {
    super(props);

    this.state = {
      rootWidth: props.dimensions.body - PADDING,
    };

    this.element = {
      root: null,
    };

    this.resizeObserver = null;
  }

  componentDidMount() {
    this.resizeObserver = new ResizeObserver(entries => {
      if (entries.length) {
        const rootWidth = entries[0].contentRect.width;
        if (rootWidth) {
          this.setState({
            rootWidth,
          });
        }
      }
    });

    this.resizeObserver.observe(this.element.root);
  }

  componentWillUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  renderItems() {
    const {
      ItemComponent,
      ItemChildren,
      itemKey,
      itemProps,
      itemType,
      itemWidth,
      items,
      showAll,
      onItemClick,
      prefixItems,
      variableWidth,
      withAdverts,
      hasValidSubscription,
    } = this.props;
    const { rootWidth } = this.state;

    const limit = Math.floor(rootWidth / itemWidth) || 1;
    const itemsToRender = showAll ? items : items.slice(0, limit);

    const itemClassNames = className('search-results-matrix__item', {
      'search-results-matrix__item--variable-width': variableWidth,
      'search-results-matrix__item--centered': variableWidth && showAll,
    });

    const renderedItems = itemsToRender.map(item => (
      <div key={item[itemKey]} className={itemClassNames}>
        <ItemComponent item={item} onClick={onItemClick} {...itemProps}>
          <ItemChildren item={item} itemKey={itemKey} itemType={itemType} />
        </ItemComponent>
      </div>
    ));

    // Splice ads into the feed
    if (withAdverts && !hasValidSubscription) {
      // First ad is on the 2nd row, unless its the mobile list in which case its the 5th.
      const firstAdPosition = limit === 1 ? 5 : limit;
      // Show ads every 5 rows. Unless its the mobile list in which case every 20.
      const adInterval = limit === 1 ? 20 : limit * 5 + 1;
      for (let i = firstAdPosition; i < renderedItems.length; i += adInterval) {
        renderedItems.splice(
          i,
          0,
          <div key={`advert-${i}`} className="search-results-matrix__advert">
            <Advert
              slotName="search-leaderboard"
              format="leaderboard"
              fullWidthResponsive={false}
              className="margin-top-10 margin-bottom-20"
              ezoicPlaceholderId={
                EZOIC_INFINITE_SCROLL_PLACEMENT_IDS[
                  ((i - firstAdPosition) / adInterval) % EZOIC_INFINITE_SCROLL_PLACEMENT_IDS.length
                ]
              }
              lazy
            />
          </div>,
        );
      }
    }

    return [
      ...prefixItems.map((item, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={`prefix-${i}`} className={itemClassNames}>
          {item}
        </div>
      )),
      ...renderedItems,
    ];
  }

  render() {
    const { children, itemWidth, itemHeight, itemType, variableWidth, showAll, items } = this.props;
    const { rootWidth } = this.state;

    const limit = Math.floor(rootWidth / itemWidth) || 1;
    const rootClassName = className(
      `search-results-matrix search-results-matrix--items-${limit} ${
        itemType === 'recipe' ? 'recipes-list-margin' : null
      }`,
      {
        'search-results-matrix--grid': variableWidth && showAll,
      },
    );
    const needSpaceForAlacarte = items
      .filter(item => item.label === 'book')
      .some(item => item.is_alacarte);
    const style =
      itemHeight && !showAll
        ? {
            height: needSpaceForAlacarte ? itemHeight + 5 : itemHeight,
            overflow: 'hidden',
            paddingTop: needSpaceForAlacarte ? 5 : 0,
          }
        : null;

    return (
      <Fragment>
        {this.props.headingRenderer(limit)}
        <div
          className={rootClassName}
          ref={_root => {
            this.element.root = _root;
          }}
          data-cy={`search-result-section-${itemType}`}
          style={style}
        >
          {this.renderItems()}
          {children}
        </div>
      </Fragment>
    );
  }
}

const mapStateToProps = state => ({
  dimensions: state.dimensions,
  // eslint-disable-next-line camelcase
  hasValidSubscription: state.user.subscription?.is_valid || false,
});

export default connect(
  mapStateToProps,
  null,
)(withStyles(CssSearchResultsMatrix)(SearchResultsMatrix));
